const DB_NAME = 'VoiceTodoOfflineDB';
const DB_VERSION = 2;
const STORE_NAME = 'pending_sync_queue';
const MEMO_STORE_NAME = 'task_voice_memos';

export function generateUUID() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function openOfflineDB() {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      resolve(null);
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(MEMO_STORE_NAME)) {
        db.createObjectStore(MEMO_STORE_NAME, { keyPath: 'task_id' });
      }
    };
    request.onsuccess = (e) => resolve(e.target.result);
    request.onerror = (e) => reject(e.target.error);
  });
}

export async function addPendingTaskToOfflineDB(task) {
  try {
    const db = await openOfflineDB();
    if (!db) return;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.put({
        ...task,
        queued_at: new Date().toISOString()
      });
      tx.oncomplete = () => resolve(true);
      tx.onerror = (e) => reject(e.target.error);
    });
  } catch (err) {
    console.error('addPendingTaskToOfflineDB error:', err);
  }
}

export async function getPendingTasksFromOfflineDB() {
  try {
    const db = await openOfflineDB();
    if (!db) return [];
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = (e) => reject(e.target.error);
    });
  } catch (err) {
    console.error('getPendingTasksFromOfflineDB error:', err);
    return [];
  }
}

export async function removePendingTaskFromOfflineDB(id) {
  try {
    const db = await openOfflineDB();
    if (!db) return;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.delete(id);
      tx.oncomplete = () => resolve(true);
      tx.onerror = (e) => reject(e.target.error);
    });
  } catch (err) {
    console.error('removePendingTaskFromOfflineDB error:', err);
  }
}

export async function clearPendingTasksOfflineDB() {
  try {
    const db = await openOfflineDB();
    if (!db) return;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.clear();
      tx.oncomplete = () => resolve(true);
      tx.onerror = (e) => reject(e.target.error);
    });
  } catch (err) {
    console.error('clearPendingTasksOfflineDB error:', err);
  }
}

// ===== REQ-056: Task Voice Memo Attachments in IndexedDB =====
export async function saveVoiceMemo(taskId, audioBlob, durationSeconds) {
  try {
    const db = await openOfflineDB();
    if (!db) return;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(MEMO_STORE_NAME, 'readwrite');
      const store = tx.objectStore(MEMO_STORE_NAME);
      store.put({
        task_id: taskId,
        audio_blob: audioBlob,
        duration: durationSeconds,
        created_at: new Date().toISOString()
      });
      tx.oncomplete = () => resolve(true);
      tx.onerror = (e) => reject(e.target.error);
    });
  } catch (err) {
    console.error('saveVoiceMemo error:', err);
  }
}

export async function getVoiceMemo(taskId) {
  try {
    const db = await openOfflineDB();
    if (!db) return null;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(MEMO_STORE_NAME, 'readonly');
      const store = tx.objectStore(MEMO_STORE_NAME);
      const request = store.get(taskId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = (e) => reject(e.target.error);
    });
  } catch (err) {
    console.error('getVoiceMemo error:', err);
    return null;
  }
}

export async function deleteVoiceMemo(taskId) {
  try {
    const db = await openOfflineDB();
    if (!db) return;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(MEMO_STORE_NAME, 'readwrite');
      const store = tx.objectStore(MEMO_STORE_NAME);
      store.delete(taskId);
      tx.oncomplete = () => resolve(true);
      tx.onerror = (e) => reject(e.target.error);
    });
  } catch (err) {
    console.error('deleteVoiceMemo error:', err);
  }
}

